import React from 'react';
import { Eye, Pencil, Phone, Mail, MapPin, UserCheck, UserX } from 'lucide-react';
import { formatTeamRoleLabel, formatInstallationTrade, getRoleBadgeStyle } from '../utils/teamMemberLabels';

export const TeamGridView = ({ data, isLoading, onView, onEdit, onToggleStatus }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-100 p-4 animate-pulse min-w-0">
            <div className="flex gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-slate-100" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-slate-100 rounded w-1/4" />
                <div className="h-4 bg-slate-100 rounded w-3/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="py-16 text-center text-slate-400 font-bold text-xs tracking-wide">
        No team members found
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 min-w-0">
      {data.map((employee) => {
        const off = employee.status === 'Inactive';
        return (
          <div
            key={employee._id}
            className={`group bg-white rounded-xl border border-slate-200/80 p-5 transition-all duration-300 hover:shadow-md hover:border-slate-300 flex flex-col gap-4 ${
              off ? 'opacity-85 grayscale-[0.2]' : ''
            }`}
          >
            {/* Top Row: Avatar + Identity + Status */}
            <div className="flex items-start justify-between gap-2 min-w-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <div
                  className={`w-10 h-10 text-xs rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-sm border ${
                    off 
                      ? 'bg-slate-500 text-white border-slate-600/60' 
                      : 'bg-slate-900 text-white border-slate-700/60'
                  }`}
                >
                  {employee.firstName?.substring(0, 1)?.toUpperCase() || 'U'}
                </div>
                <div className="min-w-0">
                  <div className="text-[9px] font-extrabold text-slate-400 uppercase tracking-widest leading-none">
                    #{employee.employeeId?.toUpperCase() || 'EMP'}
                  </div>
                  <h3 className="text-[13px] font-bold text-slate-800 truncate leading-tight mt-1.5 font-serif">
                    {employee.firstName} {employee.lastName}
                  </h3>
                  <div className="inline-flex items-center mt-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${getRoleBadgeStyle(employee.role)}`}>
                      {employee.role === 'installation_team' && employee.installationTrade ? formatInstallationTrade(employee.installationTrade) : formatTeamRoleLabel(employee.role)}
                    </span>
                  </div>
                </div>
              </div>

              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${
                  (employee.status || 'Active') === 'Active'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200/30'
                    : 'bg-slate-50 text-slate-500 border-slate-200/30'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                  (employee.status || 'Active') === 'Active' ? 'bg-emerald-500' : 'bg-slate-400'
                }`} />
                {employee.status || 'Active'}
              </span>
            </div>

            {/* Contact Info: Two Column Grid */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-slate-50/60 border border-slate-100 rounded-lg p-2.5 flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-1 text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                  <Phone size={10} className="text-slate-400 shrink-0" />
                  <span>Phone</span>
                </div>
                <div className="text-[11px] font-semibold text-slate-700 truncate leading-none">
                  {employee.phone || '—'}
                </div>
              </div>
              <div className="bg-slate-50/60 border border-slate-100 rounded-lg p-2.5 flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-1 text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                  <Mail size={10} className="text-slate-400 shrink-0" />
                  <span>Email</span>
                </div>
                <div className="text-[11px] font-semibold text-slate-700 truncate leading-none">
                  {employee.email ? `${employee.email.split('@')[0]}@...` : '—'}
                </div>
              </div>
            </div>

            {/* Details Section: List Style */}
            <div className="flex flex-col gap-2 pt-1">
              <div className="flex items-center justify-between gap-3 text-[11px]">
                <div className="flex items-center gap-1.5 text-slate-400 font-bold uppercase tracking-wider text-[9px]">
                  <MapPin size={12} className="shrink-0" />
                  <span>Location</span>
                </div>
                <span className="font-semibold text-slate-700 truncate max-w-[130px]">
                  {employee.workLocation || 'N/A'}
                </span>
              </div>
              
              <div className="border-t border-slate-100 my-1 w-full" />

              {/* <div className="flex items-center justify-between gap-3 text-[11px]">
                <div className="flex items-center gap-1.5 text-slate-400 font-bold uppercase tracking-wider text-[9px]">
                  <UserCheck size={12} className="shrink-0" />
                  <span>Reports To</span>
                </div>
                <span className="font-semibold text-slate-700 truncate max-w-[130px]">
                  {employee.reportingManager || 'N/A'}
                </span>
              </div> */}
            </div>

            {/* Footer: Right Aligned Actions */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 bg-slate-50/50 -mx-5 -mb-5 px-5 py-3 rounded-b-xl">
              <button
                type="button"
                onClick={() => onView && onView(employee)}
                className="w-8 h-8 rounded-lg bg-white border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-400 flex items-center justify-center transition-all duration-200 hover:shadow-sm cursor-pointer active:scale-95 shadow-[0_2px_4px_rgba(0,0,0,0.02)]"
                title="View Details"
              >
                <Eye size={13} />
              </button>
              
              <button
                type="button"
                onClick={() => onEdit && onEdit(employee)}
                className="w-8 h-8 rounded-lg bg-white border border-slate-200 text-amber-600 hover:text-white hover:bg-amber-500 hover:border-amber-500 flex items-center justify-center transition-all duration-200 hover:shadow-md cursor-pointer active:scale-95 shadow-[0_2px_4px_rgba(0,0,0,0.02)]"
                title="Edit Member"
              >
                <Pencil size={13} />
              </button>
              
              <button
                type="button"
                onClick={() => onToggleStatus && onToggleStatus(employee)}
                className={`w-8 h-8 rounded-lg bg-white border flex items-center justify-center transition-all duration-200 hover:shadow-md cursor-pointer active:scale-95 shadow-[0_2px_4px_rgba(0,0,0,0.02)] ${
                  off 
                    ? 'border-slate-200 text-emerald-600 hover:text-white hover:bg-emerald-500 hover:border-emerald-500' 
                    : 'border-slate-200 text-rose-500 hover:text-white hover:bg-rose-500 hover:border-rose-500'
                }`}
                title={off ? 'Enable Member' : 'Disable Member'}
              >
                {off ? <UserCheck size={13} /> : <UserX size={13} />}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
